import React, { useEffect, useState } from "react";
import ChangelogModal from "@components/Changelog/ChangelogModal";
import { CalendarPlus } from "lucide-react";
import { useGetChangelogsQuery } from "@/redux/api/externalApiSlice";
import { useTranslation } from "react-i18next";
import { Collapse, Menu } from "@mui/material";

import "./changelog.sass";
import { Changelog as ChangelogType } from "@/types/general";
import dayjs from "dayjs";
import { cn } from "@helpers/utils";

export default function Changelog() {
    const { t, i18n } = useTranslation("", {keyPrefix: "help.changelog"});

    const [isChangelogModalOpen, setIsChangelogModalOpen] = useState(false);
    const [selectedChangelog, setSelectedChangelog] = useState<ChangelogType.Root>();
    const [highlightLatestChangelog, setHighlightLatestChangelog] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement>();

    const isMenuOpen = Boolean(anchorEl);

    const { data: changelogs = [], isLoading: isChangelogsLoading } = useGetChangelogsQuery({
        locale: "en",
        itemsPerPage: 3,
        currentPage: 1
    });

    const latestChangelog = changelogs[0];

    useEffect(() => {
        if (!latestChangelog || isChangelogsLoading) return;

        const wasLatestChangelogReviewed = localStorage.getItem("latestChangelogReviewedVersion") === latestChangelog.Version;

        const isFirstLaunch: boolean = localStorage.getItem("isFirstLaunch") === "true";

        if (!wasLatestChangelogReviewed) {
            setHighlightLatestChangelog(true);
        }

        if (isFirstLaunch) {
            localStorage.setItem("isFirstLaunch", "true");
            setHighlightLatestChangelog(false);
        }
    }, [latestChangelog, isChangelogsLoading]);

    const handleOpenChangelog = () => {
        setIsChangelogModalOpen(true);
        setHighlightLatestChangelog(false);
        setSelectedChangelog(latestChangelog);

        localStorage.setItem("latestChangelogReviewedVersion", latestChangelog.Version);
    };


    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(undefined);
    };

    const handleSelectChangelog = (changelog: ChangelogType.Root) => {
        setSelectedChangelog(changelog);
        setIsChangelogModalOpen(true);
        handleMenuClose();

        if (latestChangelog && changelog.Version === latestChangelog.Version) {
            setHighlightLatestChangelog(false);
            localStorage.setItem("latestChangelogReviewedVersion", latestChangelog.Version);
        }
    };

    const handleCloseModal = () => {
        setIsChangelogModalOpen(false);
    };

    // --- Date Formatting using dayjs ---
    const formatDate = (dateString: string): string => {
        const date = dayjs(dateString);
        if (!date.isValid()) {
            return dateString; // Return original string if parsing fails
        }
        // Use the current i18n language and a localized format ('ll' = 'MMM D, YYYY')
        return date.locale(i18n.language).format("ll");
    };

    return (
        <div className="z-[50] fixed bottom-[15px] left-[15px]">
            <Collapse in={highlightLatestChangelog && latestChangelog && !isChangelogsLoading}>
                <div
                    className={"text-sm font-medium w-[200px] bg-[#F3F2EF] border border-solid border-gray-300 rounded-[8px] p-[8px] text-accent cursor-pointer mb-2 glare-highlight"}
                    onClick={handleOpenChangelog}
                >
                    {latestChangelog?.Title}
                </div>
            </Collapse>
            <div
                className={cn(`w-[40px] h-[40px] rounded-full border border-accent border-solid flex items-center justify-center text-accent cursor-pointer transition-[.25s]`, isMenuOpen ? "bg-white" : "bg-gray-50")}
                onClick={handleMenuOpen}
            >
                <CalendarPlus size={14} />
            </div>

            {
                selectedChangelog &&
                <ChangelogModal
                    isOpen={isChangelogModalOpen}
                    onClose={handleCloseModal}
                    changelog={selectedChangelog}
                />
            }

            <Menu
                id="changelog-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                MenuListProps={{ "aria-labelledby": "changelog-button" }}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        width: "250px"
                    },
                    className: "border border-accent border-solid rounded-lg shadow-md"
                }}
            >
                <div className="p-[14px]">
                    {changelogs.length ?
                        <div>
                            <div className="text-center text-accent text-sm mb-2">{t("listTitle")}</div>
                            {
                                changelogs.map((log) => (
                                    <div key={log.Version} onClick={() => handleSelectChangelog(log)}
                                         className={"hover:bg-gray-50 hover:rounded-lg p-2 hover:cursor-pointer"}>
                                        <div className={"text-sm text-accent font-medium"}>
                                            {log.Title}
                                        </div>
                                        <div className={"text-xs text-accent"}>{formatDate(log.publishedAt)} -
                                            v{log.Version}</div>
                                    </div>
                                ))
                            }
                        </div>
                        :
                        <div className="text-center text-accent text-sm">
                            {t("noUpdates")}
                        </div>
                    }
                </div>
            </Menu>
        </div>
    );
}